<template>
  <PageContent
    class="tw-overflow-hidden"
  >
    <div>
      <div class="tw-my-3 lg:tw-mt-6 lg:tw-mb-7">
        <h3 class="page-title heading-color">
          News
        </h3>
      </div>
      <CompareMenu
        :nav-items="navItems"
        :selected-nav-item="selectedNavItem"
      />
      <div class="tw-mt-2.5 lg:tw-mt-7">
        <ul
          ref="articleList"
          :class="listClass"
        >
          <li
            v-for="article in articles"
            :key="article.id"
            :class="listItemClass"
          >
            <PostLink
              :post="article"
              :show-card="true"
              :show-time-ago="true"
            />
          </li>
        </ul>
      </div>
    </div>
  </PageContent>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { useNewsStore } from '@@/stores/News';
import InfiniteScrollMixin from '@@/utils/Components/InfiniteScrollMixin';
import NewsCategories from '@@/components/News/NewsCategories';
import { parseOpenMountainApiError } from '@@/utils/CommonUtils';

const getCategory = (route) => route.params.category
  ? Object.values(NewsCategories).find(({ slug }) => slug === route.params.category)
  : null;

export default {
  name: 'NewsListPage',

  mixins: [InfiniteScrollMixin],

  async setup() {
    const newsStore = useNewsStore();
    const route = useRoute();
    const category = getCategory(route);
    const params = { category: category?.code, limit: 20, reset: true, skip: 0 };
    const { error } = await useAsyncData(() => newsStore.fetchArticles(params));

    if (error?.value) {
      throw createError(parseOpenMountainApiError(error.value));
    }
  },

  computed: {
    ...mapState(useNewsStore, ['articles']),

    category() {
      return getCategory(this.$route);
    },

    categoryCode() {
      return this.category?.code;
    },

    categoryName() {
      return this.category?.name;
    },

    listClass() {
      return 'tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-y-4 md:tw-gap-x-4';
    },

    listItemClass() {
      return 'tw-h-64 lg:tw-h-80';
    },

    navItems() {
      const createNavItem = ({ name, slug }) => ({
        text: name,
        to: `/news/${slug}`,
      });

      return [
        { text: 'All', to: '/news' },
        createNavItem(NewsCategories.companyUpdates),
        createNavItem(NewsCategories.videosAndPodcasts),
        createNavItem(NewsCategories.snowAndWeatherScience),
        createNavItem(NewsCategories.snowpackUpdates),
        createNavItem(NewsCategories.tripReports),
        createNavItem(NewsCategories.resortGuides),
        createNavItem(NewsCategories.other),
      ];
    },

    selectedNavItem() {
      return this.navItems.findIndex(({ to }) => to === this.$route.path);
    },
  },

  mounted() {
    if (this.articles.length) {
      this.addIntersectionObserver('articleList', () => this.handleFetchArticles());
    }
  },

  methods: {
    ...mapActions(useNewsStore, ['fetchArticles', 'resetArticles']),

    async handleFetchArticles() {
      const { limit, skip } = this;
      const params = { category: this.categoryCode, limit, skip };
      const count = await this.fetchArticles(params);
      return count;
    },
  },
};
</script>
